import { AlertSeverity } from "@/models/AlertRule";

let audioContext: AudioContext | null = null;

function getAudioContext(): AudioContext {
  if (!audioContext) {
    audioContext = new AudioContext();
  }
  return audioContext;
}

let audioPrimed = false;

/**
 * Browser autoplay policy keeps an AudioContext "suspended" until it is created/resumed from a
 * user gesture. The Battery Runaway siren fires from a data refetch (no gesture), so without
 * priming it would silently never play. Call once on page mount: it resumes the context on the
 * user's first interaction, unlocking later event-driven sirens.
 */
export function primeAudioContext(): void {
  if (audioPrimed) return;
  audioPrimed = true;
  const unlock = () => {
    try {
      const ctx = getAudioContext();
      if (ctx.state === "suspended") ctx.resume();
    } catch {
      // AudioContext unavailable (SSR/tests) — nothing to unlock.
    }
    window.removeEventListener("pointerdown", unlock);
    window.removeEventListener("keydown", unlock);
  };
  window.addEventListener("pointerdown", unlock);
  window.addEventListener("keydown", unlock);
}

export default function playAlertSound(severity: AlertSeverity): void {
  try {
    const ctx = getAudioContext();
    if (ctx.state === "suspended") {
      ctx.resume();
    }

    const config = {
      info: { frequency: 520, duration: 0.15, beeps: 1 },
      warning: { frequency: 660, duration: 0.15, beeps: 2 },
      critical: { frequency: 880, duration: 0.2, beeps: 3 },
    }[severity];

    const now = ctx.currentTime;

    for (let i = 0; i < config.beeps; i++) {
      const startTime = now + i * (config.duration + 0.1);
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.type = "sine";
      oscillator.frequency.value = config.frequency;
      gainNode.gain.setValueAtTime(0.3, startTime);
      gainNode.gain.exponentialRampToValueAtTime(
        0.01,
        startTime + config.duration,
      );

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.start(startTime);
      oscillator.stop(startTime + config.duration);
    }
  } catch {
    // Silently fail if audio is blocked by browser autoplay policy
  }
}

let alarmInterval: ReturnType<typeof setInterval> | null = null;

/**
 * Loud, looping two-tone square-wave siren for the Battery Runaway catastrophic alarm.
 * Idempotent — calling again while already running is a no-op. Stop with
 * {@link stopBatteryRunawayAlarm}.
 */
export function startBatteryRunawayAlarm(): void {
  if (alarmInterval !== null) return;
  try {
    const ctx = getAudioContext();
    if (ctx.state === "suspended") {
      ctx.resume();
    }

    const burst = () => {
      const now = ctx.currentTime;
      const tones: [number, number][] = [
        [950, 0],
        [620, 0.25],
      ];
      for (const [frequency, offset] of tones) {
        const startTime = now + offset;
        const oscillator = ctx.createOscillator();
        const gainNode = ctx.createGain();

        oscillator.type = "square";
        oscillator.frequency.value = frequency;
        gainNode.gain.setValueAtTime(0.4, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.22);

        oscillator.connect(gainNode);
        gainNode.connect(ctx.destination);

        oscillator.start(startTime);
        oscillator.stop(startTime + 0.22);
      }
    };

    burst();
    alarmInterval = setInterval(burst, 550);
  } catch {
    // Silently fail if audio is blocked by browser autoplay policy
  }
}

export function stopBatteryRunawayAlarm(): void {
  if (alarmInterval !== null) {
    clearInterval(alarmInterval);
    alarmInterval = null;
  }
}
